import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AmaLogo from "@/components/app/AmaLogo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Shield } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Benvenuto in AMA ServiceFlow");
      if (u?.must_change_password) {
        nav("/cambia-password-obbligatorio");
      } else {
        nav("/");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenziali non valide");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="relative hidden md:flex flex-col justify-between p-10 text-white overflow-hidden" style={{ background: "linear-gradient(160deg,#0a0a2e 0%, #3a0e5e 55%, #6f42c1 100%)" }}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.35) 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div className="relative z-10">
          <AmaLogo size={44} />
        </div>
        <div className="relative z-10 space-y-4">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-pink-200/80">
            <Shield className="w-4 h-4" /> Piattaforma interna di assistenza tecnica
          </div>
          <h1 className="text-4xl lg:text-5xl font-heading font-bold leading-tight">
            <span className="bg-gradient-to-r from-pink-300 to-fuchsia-300 bg-clip-text text-transparent">Stay Worry Free.</span>
            <br />
            Ogni prodotto in assistenza, tracciato al minuto.
          </h1>
          <p className="text-pink-100/85 max-w-lg leading-relaxed">
            Accettazione multi-prodotto, scansione barcode e QR code, OCR delle etichette, PDF di accettazione,
            spedizioni al produttore, autorizzazioni cliente e riconsegna — dal magazzino al portale, in un unico flusso.
          </p>
        </div>
        <div className="relative z-10 text-xs text-pink-100/60">© AMA Sicurezza S.r.l. — Via Leucosia 37, Salerno</div>
      </div>

      {/* Right auth panel */}
      <div className="flex flex-col justify-center px-6 sm:px-12 py-10">
        <div className="md:hidden mb-8"><AmaLogo size={40} /></div>
        <div className="max-w-md w-full mx-auto">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.2em] text-[#d63384] font-semibold">Accesso interno</div>
            <h2 className="mt-1 text-3xl font-heading font-bold text-[#0a0a2e]">AMA ServiceFlow</h2>
            <p className="text-slate-500 mt-1 text-sm">Inserisci le credenziali fornite dall'amministratore.</p>
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input data-testid="login-email" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input data-testid="login-password" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className="mt-1" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full ama-btn-primary rounded-full">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Accedi
            </Button>
            <div className="text-right">
              <a href="/password-dimenticata" data-testid="forgot-password-link" className="text-xs text-[#d63384] hover:underline">Password dimenticata?</a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
